import React from 'react';
import { useAppSelector } from 'hooks/redux';
import {
  roundUpToNearest,
  yAxisFormatter,
} from 'components/DiagramsTab/constants';
import {
  Bar,
  BarChart,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  LabelList,
} from 'recharts';
import { Box, Typography } from '@mui/material';
import { formatNumber } from 'store/actions/mainPage/constant';

export const Diagram: React.FC = () => {
  const { statisticsData, lang } = useAppSelector((state) => state.mainPage);
  if (!statisticsData) {
    return null;
  }
  const { data } = statisticsData;
  const mainUnit = statisticsData?.export.unit;

  let maxValue = roundUpToNearest(Math.max(...data.map((item) => +item.value)));
  const xAxisTicks =
    maxValue <= 100000
      ? [0, maxValue / 4, (maxValue * 2) / 4, (maxValue * 3) / 4, maxValue]
      : [0, maxValue / 2, maxValue];

  const chartWidth = window.screen.height > 800 ? 340 : 320;
  const chartHeight =
    data.length < 5 ? data.length * 100 : Math.max(500, data.length * 40);
  const chartMaxHeight = `calc(100vh - 485px)`;

  const formattedData = data.map((item: any, index) => ({
    key: item.key,
    value: Math.floor(Number(item.value) * 10) / 10,
    id: index,
    personalUnit: item.unit,
  }));

  const CustomizedLabel = (props) => {
    const { x, y, width, value, height, id } = props;

    const item = formattedData.find(
      (item) => item.id === id && item.value === value
    );
    const personalUnit = mainUnit || item?.personalUnit;
    const indent = 1 + personalUnit?.length * 0.17;

    const isBigValue = value / maxValue > 0.7;
    const tooltipPositionsX = isBigValue ? x + width / indent : x + width + 5;
    const tooltipColor = isBigValue ? 'white' : '#8884d8';

    return (
      <text
        x={tooltipPositionsX}
        y={y + height / 2}
        fill={tooltipColor}
        textAnchor="start"
        dominantBaseline="middle"
        fontSize={12}
      >
        {formatNumber(value.toString(), lang)} {mainUnit || personalUnit}
      </text>
    );
  };

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;

      return (
        <Box
          sx={{
            backgroundColor: (theme) => theme.palette.custom.tooltipBg,
            p: 2,
            maxWidth: 160,
          }}
        >
          <Typography>
            {data.key}: {formatNumber(data.value.toString(), lang)}{' '}
            {mainUnit || data.personalUnit}
          </Typography>
        </Box>
      );
    }

    return null;
  };

  return (
    <Box
      sx={{
        flex: 1,
        svg: {
          overflow: 'initial',
        },
        maxHeight: chartMaxHeight,
        overflowY: 'auto',
        position: 'relative',
      }}
    >
      <Typography sx={{ textAlign: 'center', fontWeight: 700 }}>
        {statisticsData?.export.filename}
      </Typography>
      <BarChart
        width={chartWidth}
        height={chartHeight}
        data={formattedData}
        layout="vertical"
        style={{ marginLeft: '12px' }}
      >
        <CartesianGrid horizontal={false} vertical={true} />
        <XAxis
          type="number"
          dataKey="value"
          ticks={xAxisTicks}
          tickFormatter={(value) => formatNumber(value.toString(), lang)}
          style={{ fontSize: '0.8rem' }}
          interval={0}
          domain={[0, maxValue]}
        />
        <YAxis
          dataKey="key"
          type="category"
          tickFormatter={yAxisFormatter}
          style={{ fontSize: '0.8rem' }}
          width={160}
        />

        <Tooltip content={<CustomTooltip />} />
        <Bar dataKey="value" fill="#66B2FF" strokeWidth={1}>
          <LabelList
            dataKey="value"
            content={<CustomizedLabel />}
            position="right"
          />
        </Bar>
      </BarChart>
      <Typography sx={{ textAlign: 'center', color: 'rgb(102, 102, 102)' }}>
        {mainUnit}
      </Typography>
    </Box>
  );
};
